import { PinIcon, PlayIcon, LockIcon, ChevronIcon } from './icons'
import { VideoPlayer } from './VideoPlayer'
import { useHubLang } from '../lib/i18n'
import { HOME } from '../lib/hubCopy'

/**
 * The launch announcement card, with the announcement video.
 *
 * ── 5 Oct: THE VIDEO NO LONGER WAITS FOR A PINNED POST ──────────────────────
 *
 * This card used to render only when a post was pinned, and prod has never had
 * one (dev's is seed 001 demo content, and nothing in the app can pin). So on
 * oneconetec.com there was no video at all, and the welcome modal's "Watch the
 * announcement" card sent people to a hub with nothing to watch. David's call:
 * the video gets its own card. With a pinned post it is that post's card, as
 * before; without one, `postedAgo` and `body` are absent and it is the video,
 * its title and the do-not-share note — no Pinned flag, because nothing is.
 *
 * B01 decided how this video is delivered, and the decision has a shape that is
 * easy to undo by accident:
 *
 *   - Private Vimeo, Starter tier or above. Supabase Storage was ruled out —
 *     no adaptive bitrate, and a 400 MB mp4 pushed at a crew tethered to a
 *     phone in a gravel pit is not a video, it is a spinner. Microsoft Stream
 *     was ruled out because ~40% of staff hold no Microsoft licence and no hub
 *     account carries Microsoft credentials.
 *   - The video stays gated behind hub sign-in. There is no public link and
 *     there must not be a "share" affordance here — this is internal ahead of
 *     the external launch in April 2027, and a public Vimeo URL is a leak with
 *     a nice player attached.
 *
 * ── CORRECTION, 30 Aug: `embedUrl` IS NOT SIGNED AND DOES NOT EXPIRE ────────
 *
 * This prop was previously documented as a "signed, expiring embed for the
 * signed-in viewer". That was never true and could not be — Vimeo does not mint
 * signed per-viewer URLs at this tier. The real gates are the hub login (which
 * keeps the page unreachable signed-out) and Vimeo's domain-level privacy
 * (a Referer check that keeps the embed from playing anywhere but our host).
 * Full reasoning, including why `Referrer-Policy: no-referrer` breaks it, is in
 * lib/video.ts.
 *
 * The distinction matters for one reason: the URL is in a public bundle and is
 * not a secret, so nobody should build anything on the belief that it is.
 *
 * `embedUrl` absent still renders the locked state rather than an empty box,
 * and never falls back to a public URL. If a future change needs one, that is a
 * new answer on B01, not an edit here.
 *
 * ── LAYOUT, 31 Aug: THE VIDEO IS THE POST ───────────────────────────────────
 *
 * This was a two-column card — a 320px video beside a column of text. Two
 * things were wrong with it and they compounded:
 *
 *   - 320px wide at 16/9 is a 180px box, and the control bar is ~64px of that,
 *     so the picture was ~116px and the bar's own children (two 48px buttons,
 *     a seek, a clock, a volume slider, fullscreen) did not fit across 320px.
 *     Controls fell off the end of the bar. The fix is in hub.css: the frame
 *     carries the aspect ratio now, so the bar ADDS height beneath the picture
 *     rather than eating into it.
 *   - Beside a wall of text, the video read as an illustration. People arrive
 *     at a feed expecting to scroll video, not to read a page with a clip on
 *     it. So the video is full card width, edge to edge, and the prose is
 *     behind a disclosure.
 *
 * The title stays visible — it is one line and it is what the video is about.
 * The do-not-share note stays visible too, and deliberately did NOT move into
 * the disclosure: B01 keeps this internal until April 2027, and a warning
 * nobody expands is a warning nobody reads.
 */
export function PinnedAnnouncement({
  title,
  body,
  postedAgo,
  embedUrl,
  videoTitle,
  durationLabel,
  countViewsAs,
}: {
  title: string
  /** Absent when no post is pinned: no disclosure is drawn. */
  body?: string
  /** Absent when no post is pinned: no Pinned flag is drawn, because nothing is. */
  postedAgo?: string
  /** Domain-restricted Vimeo embed. Absent = show locked. Not a secret. */
  embedUrl?: string
  videoTitle?: string
  durationLabel?: string
  /** 198. The Vimeo id to count views under, once the card is shown to staff. */
  countViewsAs?: string
}) {
  const h = HOME[useHubLang()]
  return (
    <article className="pinned">
      {postedAgo !== undefined && (
        <header className="pinned__head">
          <span className="pinned__flag">
            <PinIcon size={14} />
            {h.pinned}
          </span>
          <span className="pinned__when">{postedAgo}</span>
        </header>
      )}

      <div className="pinned__body">
        <div className="pinned__video">
          {embedUrl ? (
            /* The iframe used to live here directly. It now lives inside
               VideoPlayer, which drives it over the Vimeo SDK so the controls
               can be ours and big enough to hit in gloves. Two things that
               looked like properties of this file moved with it and must not
               come back: `loading="lazy"` (a lazy iframe cannot autoplay,
               because it has not loaded when the autoplay attempt happens) and
               the absence of allow="autoplay" (muted autoplay needs it in the
               allow list, and its absence was the previous no-autoplay
               decision expressed as an attribute). The referrerPolicy is set
               on the iframe the SDK creates — see VideoPlayer. */
            <VideoPlayer
              embedUrl={embedUrl}
              title={videoTitle ?? title}
              durationLabel={durationLabel}
              countAs={countViewsAs}
            />
          ) : (
            <div className="pinned__locked">
              <PlayIcon size={28} />
              <p className="pinned__lockedtitle">{videoTitle ?? title}</p>
              <p className="pinned__lockedsub">
                <LockIcon size={13} />
                {h.lockedSub}
              </p>
            </div>
          )}
        </div>

        <div className="pinned__text">
          <h3 className="pinned__title">{title}</h3>

          {/* A native <details>, not a useState toggle. It is keyboard-operable
              and announced as expandable for free, it survives with JS broken,
              and Ctrl-F finds text inside a closed one in Chrome. A hand-rolled
              disclosure gets none of that and is more code. */}
          {body !== undefined && (
            <details className="pinned__more">
              <summary className="pinned__summary">
                <ChevronIcon size={14} />
                <span className="pinned__summarylabel">{h.readAnnouncement}</span>
              </summary>
              <p className="pinned__excerpt">{body}</p>
            </details>
          )}

          <p className="pinned__note">
            <LockIcon size={12} />
            {h.internalOnly}
          </p>
        </div>
      </div>
    </article>
  )
}
